import { t } from "i18next";
import { Image } from "@src/components/Image";
import React, { Component } from "react";
import { getAwsAssets } from "@src/utils";
import { ANDROID_SYSTEM_WEBVIEW_PLAY_STORE_URL, COMMON_DEEPLINK_ACTIONS, GOOGLE_CHROME_PLAY_STORE_URL } from "@src/utils/constants";
import { quitWebviewDeeplink } from "@src/utils/goBack";
import { hitDeeplinkExecutedV2 } from "@src/utils/hitDeeplink";
import { trackAnalyticsEvent } from "@src/utils/gtmDataLayer";
import styles from "./ErrorBoundary.module.scss"

const STACK_MAX_LENGTH = 500;

function truncateStack(stack) {
  if (!stack || typeof stack !== "string") return undefined;
  return stack.length > STACK_MAX_LENGTH
    ? `${stack.slice(0, STACK_MAX_LENGTH)}…`
    : stack;
}

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);

    // Define a state variable to track whether is an error or not
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    try {
      trackAnalyticsEvent("app_crash", {
        message: error?.message || "Unknown React error",
        stack_snippet: truncateStack(error?.stack),
        component_stack: truncateStack(errorInfo?.componentStack),
      });
    } catch {
      // Never let analytics break the fallback UI
    }
  }

  handleUpdateGoogleChrome = () => {
    hitDeeplinkExecutedV2(
      JSON.stringify({
        screen: COMMON_DEEPLINK_ACTIONS.UTIL_BROWSER,
        paramOne: GOOGLE_CHROME_PLAY_STORE_URL,
      }),
      JSON.stringify({
        screen: COMMON_DEEPLINK_ACTIONS.UTIL_BROWSER,
        paramOne: GOOGLE_CHROME_PLAY_STORE_URL,
      })
    );
  }
  handleUpdateAndroidWebview = () => {
    hitDeeplinkExecutedV2(
      JSON.stringify({
        screen: COMMON_DEEPLINK_ACTIONS.UTIL_BROWSER,
        paramOne: ANDROID_SYSTEM_WEBVIEW_PLAY_STORE_URL,
      }),
      JSON.stringify({
        screen: COMMON_DEEPLINK_ACTIONS.UTIL_BROWSER,
        paramOne: ANDROID_SYSTEM_WEBVIEW_PLAY_STORE_URL,
      })
    );
  }

  handleReload = () => {
    window.location.reload();
  }

  render() {
    // Check if the error is thrown
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className={styles.errorBoundary}>
          <div className={styles.errorBoundary__header}>
            <button
              type="button"
              className={styles.errorBoundary__back}
              onClick={() => quitWebviewDeeplink()}
              aria-label={t("components.errorBoundary.index.goBack", "Go back")}
            >
              <Image src={getAwsAssets("leftArrowIcon.svg", "layout")} alt="leftArrowIcon" height={16} width={16} />
            </button>
            <button
              type="button"
              className={styles.errorBoundary__reload}
              onClick={this.handleReload}
            >
              {t("components.errorBoundary.index.reload", "Reload again")}
            </button>
          </div>
          {/*{this?.props?.FallbackComponent()}*/}
          <div className={styles.errorBoundary__content}>
            <h1 className={styles.errorBoundary__heading}>{t("components.errorBoundary.index.updateWebviewHeading", "Please update your Android System WebView and Google Chrome from Play Store.")}</h1>
            <div style={{ margin: "20px 0" }} onClick={this.handleUpdateAndroidWebview}>
              <p className={styles.errorBoundary__text}>{t("components.errorBoundary.index.updateWebviewText", "Steps to update Android System WebView are as below:")}</p>
              <h4 className={styles.errorBoundary__clickText}>{t("components.errorBoundary.index.clickText", "CLICK HERE")}</h4>
              <p className={styles.errorBoundary__text}>{t("components.errorBoundary.index.orText", "OR")}</p>
              <ol>
                <li>{t("components.errorBoundary.index.updateWebviewLi1", `Open Play Store`)}</li>
                <li>{t("components.errorBoundary.index.updateWebviewLi2", `Search for "Android System WebView"`)}</li>
                <li>{t("components.errorBoundary.index.updateWebviewLi3", `Click on "Android System WebView"`)}</li>
                <li>{t("components.errorBoundary.index.updateWebviewLi4", `Click on "Update" Button`)}</li>
              </ol>
              <Image className={styles.errorBoundary__image} src={getAwsAssets("updateWebview.jpg", "layout")} alt="updateWebview" height={170} width={350} layout="responsive" quality={100} />
            </div>
            <div style={{ margin: "20px 0" }} onClick={this.handleUpdateGoogleChrome}>
              <p className={styles.errorBoundary__text}>{t("components.errorBoundary.index.updateWebviewText", "Steps to update Google Chrome are as below:")}</p>
              <h4 className={styles.errorBoundary__clickText}>{t("components.errorBoundary.index.clickText", "CLICK HERE")}</h4>
              <p className={styles.errorBoundary__text}>{t("components.errorBoundary.index.orText", "OR")}</p>
              <ol>
                <li>{t("components.errorBoundary.index.updateChromeLi1", `Open Play Store`)}</li>
                <li>{t("components.errorBoundary.index.updateChromeLi2", `Search for "Google Chrome"`)}</li>
                <li>{t("components.errorBoundary.index.updateChromeLi3", `Click on "Google Chrome"`)}</li>
                <li>{t("components.errorBoundary.index.updateChromeLi4", `Click on "Update" Button`)}</li>
                <li>{t("components.errorBoundary.index.updateChromeLi5", `Good luck studying`)}</li>
              </ol>
              <Image className={styles.errorBoundary__image} src={getAwsAssets("updateChrome.jpg", "layout")} alt="updateChrome" height={170} width={350} layout="responsive" quality={100} />
            </div>
          </div>
        </div>
      )
    }

    // Return children components in case of no error

    return this.props.children;
  }
}

export default ErrorBoundary;
